import Box from "@mui/material/Box";
import { Button, Dialog, DialogActions, DialogTitle, DialogContent, Stack } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useState, useEffect, useContext } from "react";
import { DataGrid } from "@mui/x-data-grid";
import { GridToolbarContainer, GridActionsCellItem } from '@mui/x-data-grid';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import TextField from '@mui/material/TextField';
import { ApiContext } from "../../../App";
import MenuItem from "@mui/material/MenuItem";
import * as React from "react";


const DEVICE_TYPES = {
    fieldserver: "fieldserver",
    fieldserver_toshiba_rbc_dxc031: "fieldserver_toshiba_rbc_dxc031",
    fieldserver_hitachi: "fieldserver_hitachi",
    shelly: "shelly",
    shelly_fm: "shelly_fm",
    disruptive: "disruptive",
    emporia: "emporia",
    airthings: "airthings",
    milesight: "milesight",
    mqtt: "mqtt",
    butlr: "butlr",
}

const DEFAULT_CONTROLLER_DATA = {
    name: "",
    address: "",
    device_type: DEVICE_TYPES.fieldserver
}

const ControllerPopUp = (props) => {
    const { t } = useTranslation();

    const { data, onChange, isOpen, onClose, onSave, errorMessages } = props;

    return (
        <Dialog open={isOpen} fullWidth maxWidth={"md"} onClose={onClose}>
            <DialogTitle>
                {t("Edit controller")}
            </DialogTitle>
            <DialogContent>
                <Stack spacing={2}>
                    <TextField
                        variant="standard"
                        id="name"
                        label={t("Name")}
                        value={data.name}
                        onChange={(event) => onChange("name", event.target.value)}
                        error={errorMessages.hasOwnProperty("name")}
                        helperText={errorMessages["name"] ? errorMessages["name"] : ""}
                    />
                    <TextField
                        variant="standard"
                        id="device_type"
                        label={t("Device type")}
                        value={data.device_type}
                        select
                        onChange={(event) => onChange("device_type", event.target.value)}
                        error={errorMessages.hasOwnProperty("device_type")}
                        helperText={errorMessages["device_type"] ? errorMessages["device_type"] : ""}
                    >
                        {Object.values(DEVICE_TYPES).map((option) => (
                            <MenuItem
                                key={option}
                                value={option}
                            >
                                {option}
                            </MenuItem>
                        ))}
                    </TextField>
                    <TextField
                        variant="standard"
                        id="address"
                        label={t("Address")}
                        value={data.address}
                        onChange={(event) => onChange("address", event.target.value)}
                        error={errorMessages.hasOwnProperty("address")}
                        helperText={errorMessages["address"] ? errorMessages["address"] : ""}
                    />
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} type="button">{t("Cancel")}</Button>
                <Button onClick={onSave} type="submit">{t("Save")}</Button>
            </DialogActions>
        </Dialog>
    )
}

const ControllerTable = (props) => {

    const { t } = useTranslation();

    const { facility } = props;

    const [errorMessages, setErrorMessages] = useState({});
    const [controllerRows, setControllerRows] = useState([])
    const [dialogIsOpen, setDialogIsOpen] = useState(false)
    const [popUpData, setPopUpData] = useState(DEFAULT_CONTROLLER_DATA)

    const { controllerApi } = useContext(ApiContext)

    useEffect(() => {
        controllerApi.list(facility,
            data => {
                setControllerRows(data)
            },
            () => window.alert(t("Couldn't connect to server"))
        )
    }, [facility])

    const deleteHandler = (id) => {
        const confirmed = window.confirm(t('Delete this controller?'))
        if (confirmed) {
            controllerApi.delete(
                id,
                () => setControllerRows(controllerRows.filter((row) => row.url !== id)),
                () => alert(t('Problem deleting controller')))
        }
    }

    const editControllerHandler = (id) => {
        setErrorMessages({})
        setDialogIsOpen(true)
        setPopUpData(controllerRows.find((row) => row.url === id))
    }

    const handleSave = () => {

        if (popUpData.url) {
            controllerApi.update(
                popUpData.url,
                popUpData,
                () => {
                    setControllerRows(
                        controllerRows.map((row) => popUpData.url === row.url ? popUpData : row)
                    );
                    setDialogIsOpen(false);
                },
                (errors) => setErrorMessages(errors)
            )
        } else {
            controllerApi.create(
                popUpData,
                (data) => {
                    setControllerRows([...controllerRows, { ...popUpData, url: data.url }])
                    setDialogIsOpen(false)
                },
                (errors) => setErrorMessages(errors)
            )
        }
    }

    const addControllerHandler = () => {
        setErrorMessages({})
        setPopUpData({ ...DEFAULT_CONTROLLER_DATA, facility: facility })
        setDialogIsOpen(true)
    }

    const handleChange = (field, value) => {
        setPopUpData({ ...popUpData, [field]: value })
    }

    const handleClose = () => {
        setDialogIsOpen(false)
    }

    const columns = [
        {
            field: "name",
            headerName: t("Name"),
            width: 350
        },
        {
            field: "device_type",
            headerName: t("Device Type"),
            width: 350
        },
        {
            field: "address",
            headerName: t("Address"),
            width: 350
        },
        {
            field: 'actions',
            type: 'actions',
            headerName: t('Actions'),
            flex: 0.5,
            minWidth: 150,
            cellClassName: 'actions',
            getActions: ({ id }) => {
                return [
                    <GridActionsCellItem
                        icon={<EditIcon />}
                        label={t("Edit")}
                        className="textPrimary"
                        onClick={() => editControllerHandler(id)}
                        color="inherit"
                    />,
                    <GridActionsCellItem
                        icon={<DeleteIcon />}
                        label={t("Delete")}
                        onClick={() => deleteHandler(id)}
                        color="inherit"
                    />,
                ];
            },
        },
    ]

    const gridToolbar = () => {
        return (
            <GridToolbarContainer>
                <Button color="primary" size="small" startIcon={<AddIcon />} onClick={addControllerHandler}>
                    {t("Add Controller")}
                </Button>
            </GridToolbarContainer>
        );
    }

    return (
        <Box sx={{ height: 400, width: '100%', marginTop: '20px' }}>
            <DataGrid
                getRowId={(row) => row.url}
                components={{
                    Toolbar: gridToolbar,
                }}
                rows={controllerRows}
                columns={columns}
                initialState={{
                    pagination: {
                        paginationModel: {
                            pageSize: 5,
                        },
                    },
                }}
                pageSizeOptions={[5]}
            />
            <ControllerPopUp
                isOpen={dialogIsOpen}
                onChange={handleChange}
                onClose={handleClose}
                errorMessages={errorMessages}
                data={popUpData}
                onSave={handleSave}
            />
        </Box>
    );
}

export default ControllerTable;